import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  FolderOpenIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import { Alert, Badge, Button, Page, PageBody, PageHeader, Spinner } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useToasts } from '../../stores/useToasts';
import {
  deleteLibraryOutput,
  duplicateLibraryOutput,
  exportStudioOutputPdf,
  getApiErrorMessage,
  getLibraryOutput,
  listLibraryVersions,
  updateStudioStatus,
} from './api';
import ApprovalControls from './components/ApprovalControls';
import BVPageTitle from './components/BVPageTitle';
import CitationPanel from './components/CitationPanel';
import ContentEditor from './components/ContentEditor';
import EvidenceSummaryPanel from './components/EvidenceSummaryPanel';
import { BVStudioOutput, BVStudioOutputVersion } from './types';
import { formatDateLabel } from './utils';

type VersionSelection = 'current' | number;

export default function BVReviewsLibraryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push: addToast } = useToasts();
  const { hasAnyPermission } = useAuth();

  const canWrite = hasAnyPermission(['bv:write', 'platform:admin']);

  const [output, setOutput] = useState<BVStudioOutput | null>(null);
  const [versions, setVersions] = useState<BVStudioOutputVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<VersionSelection>('current');
  const [selectedEditorVariantIndex, setSelectedEditorVariantIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const outputId = Number(id);

  const loadDetail = useCallback(async () => {
    if (!outputId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [nextOutput, nextVersions] = await Promise.all([
        getLibraryOutput(outputId),
        listLibraryVersions(outputId),
      ]);

      setOutput(nextOutput);
      setVersions(nextVersions.versions);
      setSelectedVersion('current');
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to load Bazaarvoice Library detail'),
      });
    } finally {
      setIsLoading(false);
    }
  }, [addToast, outputId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    setSelectedEditorVariantIndex(0);
  }, [selectedVersion, output?.id]);

  const selectedSnapshot = useMemo(() => {
    if (!output) {
      return null;
    }

    if (selectedVersion === 'current') {
      return {
        content: output.content,
        content_variants: output.content_variants,
        evidence_summary: output.evidence_summary,
        citation_manifest: output.citation_manifest,
        created_at: output.updated_at,
        label: 'Current draft',
      };
    }

    const version = versions.find(item => item.id === selectedVersion);
    if (!version) {
      return null;
    }

    return {
      content: version.content,
      content_variants: version.content_variants,
      evidence_summary: version.evidence_summary,
      citation_manifest: version.citation_manifest,
      created_at: version.created_at,
      label: `Version ${version.version_number}`,
    };
  }, [output, selectedVersion, versions]);

  const selectedSnapshotVariants = useMemo(
    () => (selectedSnapshot ? [selectedSnapshot.content, ...selectedSnapshot.content_variants] : []),
    [selectedSnapshot],
  );

  const handleDuplicate = async () => {
    if (!output) {
      return;
    }

    setIsMutating(true);
    try {
      const duplicated = await duplicateLibraryOutput(output.id);
      addToast({ kind: 'success', message: 'Library output duplicated into Studio' });
      navigate(`/bv-reviews/studio/${duplicated.id}`);
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to duplicate Bazaarvoice Library output'),
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleDelete = async () => {
    if (!output) {
      return;
    }

    if (!window.confirm(`Delete "${output.title}" from the Library?`)) {
      return;
    }

    setIsMutating(true);
    try {
      await deleteLibraryOutput(output.id);
      addToast({ kind: 'success', message: 'Library output deleted' });
      navigate('/bv-reviews/library');
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to delete Bazaarvoice Library output'),
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleExport = async () => {
    if (!output) {
      return;
    }

    setIsMutating(true);
    try {
      const { blob, filename } = await exportStudioOutputPdf(output.id);
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(blobUrl);
      addToast({ kind: 'success', message: 'Approved PDF export downloaded' });
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to export approved PDF'),
      });
    } finally {
      setIsMutating(false);
    }
  };

  const handleStatusTransition = async (status: BVStudioOutput['status'], rejectionReason?: string) => {
    if (!output) {
      return;
    }

    setIsTransitioning(true);
    try {
      const nextOutput = await updateStudioStatus(output.id, status, rejectionReason);
      setOutput(nextOutput);
      addToast({ kind: 'success', message: `Output moved to ${status.replace(/_/g, ' ')}` });
    } catch (error) {
      addToast({
        kind: 'error',
        message: getApiErrorMessage(error, 'Failed to update Library review status'),
      });
    } finally {
      setIsTransitioning(false);
    }
  };

  return (
    <Layout>
      <Page maxWidth="2xl">
        <PageHeader
          title={<BVPageTitle title={output?.title || 'Library detail'} />}
          description="Inspect grounded evidence, walk version history, and complete review or export actions."
          actions={
            <>
              <Button asChild variant="outline">
                <Link to="/bv-reviews/library">
                  <FolderOpenIcon className="mr-2 h-4 w-4" />
                  Back to Library
                </Link>
              </Button>
              {output ? (
                <>
                  <Button asChild variant="outline">
                    <Link to={`/bv-reviews/studio/${output.id}`}>
                      <ArrowPathIcon className="mr-2 h-4 w-4" />
                      Open in Studio
                    </Link>
                  </Button>
                  {canWrite ? (
                    <Button variant="outline" disabled={isMutating} onClick={handleDuplicate}>
                      <DocumentDuplicateIcon className="mr-2 h-4 w-4" />
                      Duplicate
                    </Button>
                  ) : null}
                  <Button
                    disabled={isMutating || output.status !== 'approved'}
                    onClick={handleExport}
                    title={output.status !== 'approved' ? 'Only approved outputs can be exported' : undefined}
                  >
                    <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                    Export PDF
                  </Button>
                  {canWrite ? (
                    <Button variant="outline" disabled={isMutating} onClick={handleDelete}>
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          }
        />
        <PageBody>
          {isLoading ? (
            <div className="flex min-h-[360px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : output && selectedSnapshot ? (
            <div className="space-y-6">
              {output.status !== 'approved' ? (
                <Alert variant="info" title="Export unlocks after approval">
                  Approved outputs are export-enabled. Reviewers can submit, approve, or reject the
                  current draft from this detail screen.
                </Alert>
              ) : null}

              <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-6">
                  <ContentEditor
                    title={`${output.title} · ${selectedSnapshot.label}`}
                    templateType={output.template_type}
                    status={output.status}
                    variants={selectedSnapshotVariants}
                    selectedVariantIndex={selectedEditorVariantIndex}
                    onSelectVariant={setSelectedEditorVariantIndex}
                    onTitleChange={() => {}}
                    onContentChange={() => {}}
                  />

                  {selectedVersion === 'current' ? (
                    <ApprovalControls
                      output={output}
                      canTransition={canWrite}
                      isPending={isTransitioning}
                      onTransition={handleStatusTransition}
                    />
                  ) : (
                    <Alert variant="neutral" title="Version snapshot">
                      Historical versions are read-only. Switch back to the current draft to take
                      workflow actions.
                    </Alert>
                  )}

                  <div className="grid gap-6 xl:grid-cols-2">
                    <EvidenceSummaryPanel summary={selectedSnapshot.evidence_summary} strength={output.evidence_strength} />
                    <CitationPanel citations={selectedSnapshot.citation_manifest} />
                  </div>
                </div>

                <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                  <div>
                    <p className="text-sm font-medium text-charcoal dark:text-gray-100">Version history</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Compare the current draft with immutable snapshots created during regeneration.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedVersion('current')}
                    className={`w-full rounded-xl border p-3 text-left transition-colors ${
                      selectedVersion === 'current'
                        ? 'border-eliza-red bg-eliza-red/5 dark:bg-eliza-red/10'
                        : 'border-gray-200 hover:border-gray-300 dark:border-dark-border'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-charcoal dark:text-gray-100">Current draft</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Updated {formatDateLabel(output.updated_at)}
                        </p>
                      </div>
                      <Badge variant="secondary">{output.status.replace(/_/g, ' ')}</Badge>
                    </div>
                  </button>

                  {versions.map(version => (
                    <button
                      key={version.id}
                      type="button"
                      onClick={() => setSelectedVersion(version.id)}
                      className={`w-full rounded-xl border p-3 text-left transition-colors ${
                        selectedVersion === version.id
                          ? 'border-eliza-red bg-eliza-red/5 dark:bg-eliza-red/10'
                          : 'border-gray-200 hover:border-gray-300 dark:border-dark-border'
                      }`}
                    >
                      <p className="text-sm font-medium text-charcoal dark:text-gray-100">
                        Version {version.version_number}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Saved {formatDateLabel(version.created_at)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Alert variant="error" title="Library item not found">
              The requested output could not be loaded.
            </Alert>
          )}
        </PageBody>
      </Page>
    </Layout>
  );
}
